/**
 * The shared model of the wallpaper renderer (2026-09-22): the types every style module and the
 * service use, the seeded PRNG, the 16:9 design box, the color helpers, seeded value noise and the
 * two-pass upscale. A style module (styles/<key>.ts) imports from here only, never from the service,
 * so the service can import the modules without a cycle.
 */

export type WallpaperStyle = string;

/** one of a style's two tunable parameters: an integer field from 0 to `max` (a share out of 100, or degrees) */
export interface WallpaperParam {
  key: string;
  label: string;
  /** 100 for a share, 360 for an angle */
  max: number;
  /** what the field shows after the number */
  unit: string;
  /** the value the style starts with */
  defaultValue: number;
}

export interface WallpaperStyleInfo {
  key: WallpaperStyle;
  label: string;
  /** one line for the style cards and the meta description */
  blurb: string;
  /** exactly two, in the order the fields show them */
  params: [ WallpaperParam, WallpaperParam ];
}

export const share = ( key: string, label: string, defaultValue = 50 ): WallpaperParam => ( { key, label, max: 100, unit: '/ 100', defaultValue } );
export const degrees = ( key: string, label: string, defaultValue = 0 ): WallpaperParam => ( { key, label, max: 360, unit: '°', defaultValue } );

/** mulberry32: a tiny seeded PRNG, so a URL reproduces its wallpaper exactly */
export function prng( seed: number ): () => number {
  let a = ( seed >>> 0 ) || 1;
  return () => {
    a = ( a + 0x6D2B79F5 ) >>> 0;
    let t = a;
    t = Math.imul( t ^ ( t >>> 15 ), t | 1 );
    t ^= t + Math.imul( t ^ ( t >>> 7 ), t | 61 );
    return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
  };
}

export interface Rgb { r: number; g: number; b: number; }

/** the 16:9 design box inside a canvas: as wide as the canvas (centred vertically), or as tall as it on a wider-than-16:9 canvas */
export interface DesignBox { x: number; y: number; w: number; h: number; }

export function designBox( w: number, h: number ): DesignBox {
  const ratio = 16 / 9;
  if ( w / h >= ratio ) {
    const bw = h * ratio;
    return { x: ( w - bw ) / 2, y: 0, w: bw, h };
  }
  const bh = w / ratio;
  return { x: 0, y: ( h - bh ) / 2, w, h: bh };
}

/** what every procedure gets */
export interface Scene {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  box: DesignBox;
  colors: Rgb[];
  rand: () => number;
  p: [ number, number, number ];
  seed: number;
}

export function hexToRgb( hex: string ): Rgb {
  const h = hex.replace( '#', '' );
  const n = parseInt( h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h, 16 );
  return { r: ( n >> 16 ) & 255, g: ( n >> 8 ) & 255, b: n & 255 };
}

export function rgba( c: Rgb, a: number ): string {
  return `rgba(${ c.r }, ${ c.g }, ${ c.b }, ${ a })`;
}

export function mix( a: Rgb, b: Rgb, t: number ): Rgb {
  return { r: Math.round( a.r + ( b.r - a.r ) * t ), g: Math.round( a.g + ( b.g - a.g ) * t ), b: Math.round( a.b + ( b.b - a.b ) * t ) };
}

export function luminance( c: Rgb ): number {
  return ( 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b ) / 255;
}

export function darken( c: Rgb, t: number ): Rgb {
  return mix( c, { r: 0, g: 0, b: 0 }, t );
}

export function lighten( c: Rgb, t: number ): Rgb {
  return mix( c, { r: 255, g: 255, b: 255 }, t );
}

/**
 * A soft dark edge, `strength` 0..1. It is measured on the DESIGN BOX and centred on it, never on
 * the canvas, so the middle of an ultrawide is shaded exactly like the 16:9 picture and the extra
 * width simply carries on darkening (owner, 2026-09-26).
 */
export function vignette( ctx: CanvasRenderingContext2D, box: DesignBox, w: number, h: number, strength: number ): void {
  const cx = box.x + box.w / 2, cy = box.y + box.h / 2;
  const g = ctx.createRadialGradient( cx, cy, Math.min( box.w, box.h ) * 0.35, cx, cy, Math.hypot( box.w, box.h ) * 0.62 );
  g.addColorStop( 0, 'rgba(0, 0, 0, 0)' );
  g.addColorStop( 1, `rgba(0, 0, 0, ${ strength })` );
  ctx.fillStyle = g;
  ctx.fillRect( 0, 0, w, h );
}

/**
 * Seeded 2D value noise: a lattice of `cells` x `cells` random values over the unit square, read
 * at (u, v) in 0..1 with quintic interpolation, so it is smooth (no banding) and reproducible.
 * The lattice is drawn from `rand` once; the returned function costs one interpolation per call.
 */
export function valueNoise( rand: () => number, cells: number ): ( u: number, v: number ) => number {
  const n = Math.max( 2, Math.round( cells ) );
  const grid = new Float32Array( ( n + 1 ) * ( n + 1 ) );
  for ( let i = 0; i < grid.length; i++ ) grid[ i ] = rand();
  const fade = ( t: number ) => t * t * t * ( t * ( t * 6 - 15 ) + 10 );
  return ( u: number, v: number ) => {
    const x = Math.min( n - 1e-6, Math.max( 0, u * n ) );
    const y = Math.min( n - 1e-6, Math.max( 0, v * n ) );
    const x0 = Math.floor( x ), y0 = Math.floor( y );
    const tx = fade( x - x0 ), ty = fade( y - y0 );
    const i = y0 * ( n + 1 ) + x0;
    const a = grid[ i ], b = grid[ i + 1 ], c = grid[ i + n + 1 ], d = grid[ i + n + 2 ];
    return ( a + ( b - a ) * tx ) * ( 1 - ty ) + ( c + ( d - c ) * tx ) * ty;
  };
}

/** fractal value noise: `octaves` lattices, cells doubling and weight halving each octave, normalised to 0..1 */
export function fbm( rand: () => number, baseCells: number, octaves: number, gain = 0.5 ): ( u: number, v: number ) => number {
  const layers: { n: ( u: number, v: number ) => number; a: number }[] = [];
  let cells = baseCells, amp = 1, total = 0;
  for ( let o = 0; o < octaves; o++ ) {
    layers.push( { n: valueNoise( rand, cells ), a: amp } );
    total += amp;
    cells *= 2;
    amp *= gain;
  }
  return ( u: number, v: number ) => {
    let s = 0;
    for ( const l of layers ) s += l.n( u, v ) * l.a;
    return s / total;
  };
}

/** a small offscreen canvas of `sw` x `sh` (its 2D context ready), for per-pixel or blurry work that is upscaled after */
export function offscreen( sw: number, sh: number ): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement( 'canvas' );
  canvas.width = Math.max( 2, Math.round( sw ) );
  canvas.height = Math.max( 2, Math.round( sh ) );
  return { canvas, ctx: canvas.getContext( '2d' )! };
}

/** draws a small canvas over the whole target in two smoothing passes (small → mid → full), so the interpolation stays soft at 5K */
export function upscale( ctx: CanvasRenderingContext2D, small: HTMLCanvasElement, w: number, h: number ): void {
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const mid = document.createElement( 'canvas' );
  mid.width = Math.max( small.width, Math.round( w / 4 ) );
  mid.height = Math.max( small.height, Math.round( h / 4 ) );
  const mc = mid.getContext( '2d' )!;
  mc.imageSmoothingEnabled = true;
  mc.imageSmoothingQuality = 'high';
  mc.drawImage( small, 0, 0, mid.width, mid.height );
  ctx.drawImage( mid, 0, 0, w, h );
}
